import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  Notification, NotificationType,
  listNotifications, markRead, markAllRead, deleteNotification, createNotification, CreateNotificationInput,
} from '../services/notificationsApi';

interface State {
  items: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  fetchList: (filters?: { type?: NotificationType; unread_only?: boolean; page?: number; limit?: number }) => Promise<void>;
  markRead: (id: number) => Promise<void>;
  markAllRead: () => Promise<void>;
  remove: (id: number) => Promise<void>;
  create: (input: CreateNotificationInput) => Promise<Notification>;
}

const toErr = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong');

export const useNotificationsStore = create<State>()(
  devtools(
    (set, get) => ({
      items: [],
      unreadCount: 0,
      loading: false,
      error: null,
      fetchList: async (filters) => {
        set({ loading: true, error: null });
        try {
          const result = await listNotifications(filters);
          set({ items: result.data, unreadCount: result.unread_count, loading: false });
        } catch (err) { set({ loading: false, error: toErr(err) }); }
      },
      markRead: async (id) => {
        try {
          const updated = await markRead(id);
          const items = get().items.map((n) => (n.id === id ? updated : n));
          set({ items, unreadCount: items.filter((n) => !n.is_read).length });
        } catch (err) { set({ error: toErr(err) }); }
      },
      markAllRead: async () => {
        try {
          await markAllRead();
          const items = get().items.map((n) => ({ ...n, is_read: true }));
          set({ items, unreadCount: 0 });
        } catch (err) { set({ error: toErr(err) }); }
      },
      remove: async (id) => {
        try {
          await deleteNotification(id);
          const items = get().items.filter((n) => n.id !== id);
          set({ items, unreadCount: items.filter((n) => !n.is_read).length });
        } catch (err) { set({ error: toErr(err) }); }
      },
      create: async (input) => {
        try {
          const created = await createNotification(input);
          set((s) => ({
            items: [created, ...s.items],
            unreadCount: s.unreadCount + (created.is_read ? 0 : 1),
          }));
          return created;
        } catch (err) { set({ error: toErr(err) }); throw err; }
      },
    }),
    { name: 'notifications-store' }
  )
);
